/**
 * Screen effects — camera shake, flashes, vignettes, particle bursts.
 *
 * Carried from v1 with one behavioural change that matters in an 8-player fight:
 *
 * v1's `spawnParticles` evicted the OLDEST burst whenever the budget was full,
 * and the budget was 20 on ultra / 5 on low. Eight players casting abilities
 * blow through that in under a second, so the ultimate you just fired vanished
 * because someone else landed a basic attack. Budgets are ~3x higher now
 * (PostProcessing.js) and eviction prefers a burst that is nearly finished; only
 * if every burst is fresh does it fall back to the oldest.
 */

import * as THREE from 'three/webgpu';
import { cameraShake, particles } from '../state.js';
import { getParticleBudget } from '../PostProcessing.js';
import {
  createParticleMaterial,
  createShardMaterial,
  setParticleParams,
  setParticleFade,
} from './ParticleUtils.js';

let _scene = null;

export function initScreenEffects(scene) {
  _scene = scene;
}

export function triggerCameraShake(intensity, duration) {
  // Never let a small shake cut a big one short.
  const remaining = cameraShake.duration - (Date.now() - cameraShake.startTime);
  if (remaining > 0 && cameraShake.intensity > intensity && remaining > duration) return;
  cameraShake.intensity = intensity;
  cameraShake.duration = duration;
  cameraShake.startTime = Date.now();
}

export function updateCameraShake() {
  const elapsed = Date.now() - cameraShake.startTime;
  if (elapsed >= cameraShake.duration) {
    cameraShake.offset.set(0, 0, 0);
    return;
  }
  const decay = 1 - elapsed / cameraShake.duration;
  const i = cameraShake.intensity * decay;
  cameraShake.offset.set(
    (Math.random() - 0.5) * i,
    (Math.random() - 0.5) * i,
    (Math.random() - 0.5) * i
  );
}

function createOverlay(zIndex) {
  const el = document.createElement('div');
  el.style.cssText = `position:fixed;inset:0;pointer-events:none;z-index:${zIndex};opacity:0;transition:opacity 0.3s;`;
  document.body.appendChild(el);
  return el;
}

let screenFlashEl = null;
let vignetteEl = null;
let directionalEl = null;

export function screenFlash(color, duration = 300, strength = 0.4) {
  if (!screenFlashEl) screenFlashEl = createOverlay(40);
  screenFlashEl.style.background = color;
  screenFlashEl.style.transition = 'none';
  screenFlashEl.style.opacity = String(strength);
  requestAnimationFrame(() => {
    screenFlashEl.style.transition = `opacity ${duration}ms`;
    screenFlashEl.style.opacity = '0';
  });
}

export function showVignette(color, duration = 2000, strength = 0.6) {
  if (!vignetteEl) vignetteEl = createOverlay(39);
  vignetteEl.style.background = `radial-gradient(ellipse at center, transparent 45%, ${color} 100%)`;
  vignetteEl.style.opacity = String(strength);
  clearTimeout(vignetteEl._timer);
  vignetteEl._timer = setTimeout(() => {
    vignetteEl.style.opacity = '0';
  }, duration);
}

/**
 * Damage vignette biased toward where the hit came from.
 *
 * `angle` is radians in screen space: 0 = ahead, PI/2 = to the right. Being able
 * to turn toward the shooter is the whole reason artillery is playable.
 */
export function showDirectionalDamage(angle, strength = 0.55, duration = 650) {
  if (!directionalEl) directionalEl = createOverlay(38);
  const x = 50 + Math.sin(angle) * 50;
  const y = 50 - Math.cos(angle) * 42;
  directionalEl.style.background =
    `radial-gradient(circle at ${x}% ${y}%, rgba(255,40,70,0.85) 0%, rgba(255,40,70,0.35) 22%, transparent 55%)`;
  directionalEl.style.transition = 'none';
  directionalEl.style.opacity = String(strength);
  requestAnimationFrame(() => {
    directionalEl.style.transition = `opacity ${duration}ms`;
    directionalEl.style.opacity = '0';
  });
}

/** Creeping desaturation at low HP. Set `on` false to clear it. */
let lowHpEl = null;
export function setLowHealthOverlay(on) {
  if (!lowHpEl) {
    lowHpEl = createOverlay(37);
    lowHpEl.style.background =
      'radial-gradient(ellipse at center, transparent 38%, rgba(120,0,20,0.75) 100%)';
    lowHpEl.style.transition = 'opacity 400ms';
  }
  lowHpEl.style.opacity = on ? '0.75' : '0';
}

// ─── Burst pooling ──────────────────────────────────────────────
//
// A burst used to allocate a fresh PlaneGeometry, Float32Arrays, an
// InstancedBufferAttribute and a SpriteNodeMaterial every call, and dispose the
// lot ~1s later. At 8 casting players that is a steady churn of GPU buffer
// creates/destroys plus a fresh node-material build per burst. Resources are now
// pooled per blend mode with a fixed 64-instance capacity (`mesh.count` limits
// the draw, so a 10-particle burst on a 64-slot buffer draws 10 instances); the
// material is re-aimed with uniforms (see ParticleUtils.js) instead of rebuilt.

/** Instance capacity of a pooled burst. Largest live caller uses 40. */
const MAX_BURST_COUNT = 64;
/** Free resources kept per pool key; beyond this, released bursts dispose. */
const POOL_KEEP = 24;
// Two shapes (soft circle, cel shard) × two blend modes. Four programs total —
// the shard variants exist because the reference art's debris is chunky and
// angular, and a soft round puff cannot be art-directed into that.
const _pools = { normal: [], additive: [], 'shard-normal': [], 'shard-additive': [] };

function createBurstResources(poolKey) {
  const shard = poolKey.startsWith('shard-');
  const blendKey = shard ? poolKey.slice(6) : poolKey;
  const positions = new Float32Array(MAX_BURST_COUNT * 3);
  const velocities = new Float32Array(MAX_BURST_COUNT * 3);
  const posAttr = new THREE.InstancedBufferAttribute(positions, 3);
  posAttr.setUsage(THREE.DynamicDrawUsage);
  const geo = new THREE.PlaneGeometry(1, 1);
  geo.setAttribute('burstPos', posAttr);
  const params = { color: 0xffffff, size: 0.3, opacity: 1, blending: blendKey };
  let material;
  let rotAttr = null;
  if (shard) {
    // Per-instance (rotation, size jitter) — baked at spawn, static in flight.
    rotAttr = new THREE.InstancedBufferAttribute(new Float32Array(MAX_BURST_COUNT * 2), 2);
    geo.setAttribute('burstRot', rotAttr);
    material = createShardMaterial(posAttr, rotAttr, params);
  } else {
    material = createParticleMaterial(posAttr, params);
  }
  const mesh = new THREE.Mesh(geo, material);
  mesh.frustumCulled = false;
  return { mesh, posAttr, rotAttr, velocities, poolKey };
}

/** Returns a finished burst's resources to the pool (or disposes past the cap). */
function releaseBurst(p) {
  _scene.remove(p.mesh);
  const pool = _pools[p.res.poolKey];
  if (pool.length < POOL_KEEP) {
    pool.push(p.res);
  } else {
    p.mesh.geometry.dispose();
    p.mesh.material.dispose();
  }
}

function evictOneBurst(now) {
  // Prefer a burst that is at least 60% done — deleting the tail of a fading
  // puff is invisible; deleting a just-fired ultimate is not.
  let index = -1;
  let bestAge = -1;
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const age = (now - p.startTime) / p.lifetime;
    if (age >= 0.6 && age > bestAge) {
      bestAge = age;
      index = i;
    }
  }
  if (index < 0) index = 0; // everything is fresh: fall back to oldest
  const [dead] = particles.splice(index, 1);
  releaseBurst(dead);
}

/**
 * A burst of particles.
 *
 * `opts.gravity` in units/s^2 (default 9.8), `opts.dir` gives the burst a
 * direction (used by directional impacts), `opts.upBias` scales the upward
 * component of the launch cone — vy is uniform in [0, speed * upBias], so 0.3
 * gives a low puff that rises and settles (landing dust), 2 a fountain
 * (fault line), and the default 1 the standard omnidirectional pop.
 *
 * `opts.shape: 'shard'` swaps the soft circle for the cel-banded kite.
 * `opts.emitRadius` spawns on a ground circle of that radius instead of at a
 * point, and `opts.inward` aims each particle back at the centre — together
 * they are the ANTICIPATION gather: energy converging on a caster is the
 * universal "something is about to happen" beat.
 */
export function spawnParticles(position, color, count = 20, speed = 5, opts = {}) {
  if (!_scene) return null;
  const now = Date.now();
  const budget = getParticleBudget();
  while (particles.length >= budget) evictOneBurst(now);

  const n = Math.min(count, MAX_BURST_COUNT);
  const blendKey = opts.blending === 'additive' ? 'additive' : 'normal';
  const poolKey = opts.shape === 'shard' ? `shard-${blendKey}` : blendKey;
  const res = _pools[poolKey].pop() ?? createBurstResources(poolKey);
  const positions = res.posAttr.array;
  const velocities = res.velocities;

  const px = position.x ?? position[0] ?? 0;
  const py = position.y ?? position[1] ?? 0;
  const pz = position.z ?? position[2] ?? 0;

  const dir = opts.dir ?? null;
  const upBias = opts.upBias ?? 1;
  const emitRadius = opts.emitRadius ?? 0;

  for (let i = 0; i < n; i++) {
    const i3 = i * 3;
    let vx = (Math.random() - 0.5) * speed;
    let vy = Math.random() * speed * upBias;
    let vz = (Math.random() - 0.5) * speed;
    if (emitRadius > 0) {
      const a = Math.random() * Math.PI * 2;
      const r = emitRadius * (0.85 + Math.random() * 0.15);
      positions[i3] = px + Math.sin(a) * r;
      positions[i3 + 1] = py;
      positions[i3 + 2] = pz + Math.cos(a) * r;
      if (opts.inward) {
        // Converge on the centre; the caller sizes `speed` so arrival lands
        // roughly on the strike beat.
        vx = -Math.sin(a) * speed + vx * 0.15;
        vy = vy * 0.25;
        vz = -Math.cos(a) * speed + vz * 0.15;
      } else {
        vx = Math.sin(a) * speed * 0.8 + vx * 0.3;
        vz = Math.cos(a) * speed * 0.8 + vz * 0.3;
      }
    } else {
      positions[i3] = px;
      positions[i3 + 1] = py;
      positions[i3 + 2] = pz;
    }
    if (dir) {
      vx += dir.x * speed;
      vy += dir.y * speed;
      vz += dir.z * speed;
    }
    velocities[i3] = vx;
    velocities[i3 + 1] = vy;
    velocities[i3 + 2] = vz;
  }
  res.posAttr.needsUpdate = true;
  if (res.rotAttr) {
    const rot = res.rotAttr.array;
    for (let i = 0; i < n; i++) {
      rot[i * 2] = Math.random() * Math.PI * 2;
      rot[i * 2 + 1] = 0.55 + Math.random() * 0.9;
    }
    res.rotAttr.needsUpdate = true;
  }

  setParticleParams(res.mesh.material, {
    color,
    size: opts.size ?? 0.3,
    opacity: opts.opacity ?? 1.0,
  });

  res.mesh.count = n;
  _scene.add(res.mesh);

  const burst = {
    mesh: res.mesh,
    posAttr: res.posAttr,
    velocities,
    res,
    count: n,
    startTime: now,
    lifetime: opts.lifetime ?? 1500,
    gravity: opts.gravity ?? 9.8,
    drag: opts.drag ?? 0,
  };
  particles.push(burst);
  return burst;
}

export function updateParticles(dt) {
  const now = Date.now();
  if (!dt || dt <= 0) dt = 0.016;

  for (let i = particles.length - 1; i >= 0; i--) {
    const p = particles[i];
    const elapsed = now - p.startTime;

    if (elapsed >= p.lifetime) {
      releaseBurst(p);
      particles.splice(i, 1);
      continue;
    }

    const positions = p.posAttr.array;
    // The pooled backing array is MAX_BURST_COUNT slots; only the live ones move.
    const count = p.count ?? positions.length / 3;
    const vel = p.velocities;
    const dragFactor = p.drag > 0 ? Math.pow(1 - p.drag, dt * 60) : 1;

    for (let j = 0; j < count; j++) {
      const j3 = j * 3;
      positions[j3] += vel[j3] * dt;
      positions[j3 + 1] += vel[j3 + 1] * dt;
      positions[j3 + 2] += vel[j3 + 2] * dt;
      vel[j3 + 1] -= p.gravity * dt;
      if (dragFactor !== 1) {
        vel[j3] *= dragFactor;
        vel[j3 + 1] *= dragFactor;
        vel[j3 + 2] *= dragFactor;
      }
    }

    p.posAttr.needsUpdate = true;
    setParticleFade(p.mesh.material, 1 - elapsed / p.lifetime);
  }
}

export function clearAllParticles() {
  for (const p of particles) releaseBurst(p);
  particles.length = 0;
}
